import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { Download, Share, PlusSquare, ShieldCheck, CalendarOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* Persoonlijke badge op de telefoon: /badge/<token>. Geen login — de token IS de badge. */
export default function Badge() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [png, setPng] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const canvasRef = useRef(null);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;

  useEffect(() => {
    axios.get(`${API}/api/badge/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Badge niet gevonden"));
  }, [token]);

  useEffect(() => {
    if (!info) return;
    (async () => {
      const qr = await QRCode.toDataURL(info.qr, { width: 640, margin: 1, color: { dark: "#0f172a", light: "#ffffff" } });
      setQrSrc(qr);
      const c = canvasRef.current; const ctx = c.getContext("2d");
      c.width = 1080; c.height = 1920;
      ctx.fillStyle = "#0f172a"; ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = "#f97316"; ctx.fillRect(0, 0, c.width, 18);
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 64px Arial"; ctx.textAlign = "center"; ctx.fillText(info.garage, 540, 300);
      ctx.fillStyle = "#94a3b8"; ctx.font = "32px Arial"; ctx.fillText("PERSONEELSBADGE · TIJDREGISTRATIE", 540, 360);
      const img = new Image(); img.src = qr; await new Promise((r) => { img.onload = r; });
      ctx.fillStyle = "#ffffff"; roundRect(ctx, 200, 480, 680, 680, 48); ctx.fill();
      ctx.drawImage(img, 240, 520, 600, 600);
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 72px Arial"; ctx.fillText(info.name, 540, 1300);
      ctx.fillStyle = "#f97316"; ctx.font = "36px Arial"; ctx.fillText(info.department.toUpperCase(), 540, 1370);
      ctx.fillStyle = "#64748b"; ctx.font = "28px Arial"; ctx.fillText("Scan aan de kiosk om in of uit te klokken", 540, 1560);
      setPng(c.toDataURL("image/png"));
    })();
  }, [info]);

  const share = async () => {
    try {
      const blob = await (await fetch(png)).blob();
      const file = new File([blob], `badge-${info.name}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: `Badge ${info.name}` });
      else window.open(png, "_blank");
    } catch { /* user cancelled */ }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-950 text-white p-8 text-center" data-testid="badge-error"><div><ShieldCheck className="h-12 w-12 mx-auto text-rose-500 mb-4" /><h1 className="text-xl font-bold">{err}</h1><p className="text-slate-400 mt-2 text-sm">Vraag een nieuwe badge-link aan bij de receptie.</p></div></div>;
  if (!info) return <div className="min-h-screen bg-slate-950" />;

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center px-5 py-8" data-testid="badge-page">
      <canvas ref={canvasRef} className="hidden" />
      <div className="w-full max-w-sm rounded-[28px] overflow-hidden shadow-2xl border border-slate-800 bg-slate-900" data-testid="badge-card">
        <div className="h-1.5 bg-orange-500" />
        <div className="p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-1"><img src="/logo-v12.png" alt="" className="h-8 rounded" /><span className="font-bold text-lg">{info.garage}</span></div>
          <div className="text-[10px] uppercase tracking-[.25em] text-slate-400">Personeelsbadge</div>
          <div className="mx-auto mt-5 rounded-3xl bg-white p-4 w-64 h-64 grid place-items-center">{qrSrc ? <img src={qrSrc} alt="QR" className="w-56 h-56" data-testid="badge-qr" /> : null}</div>
          <div className="mt-5 text-2xl font-black" data-testid="badge-name">{info.name}</div>
          <div className="text-orange-400 text-sm font-semibold tracking-wide">{info.department}</div>
        </div>
      </div>

      <div className="w-full max-w-sm mt-6 grid grid-cols-2 gap-3">
        <a href={png} download={`badge-${info.name}.png`} className="rounded-2xl bg-orange-500 hover:bg-orange-400 active:scale-[.98] transition p-4 flex flex-col items-center gap-2 text-sm font-semibold" data-testid="badge-download"><Download className="h-6 w-6" />Opslaan als afbeelding</a>
        <button onClick={share} className="rounded-2xl bg-slate-800 hover:bg-slate-700 active:scale-[.98] transition p-4 flex flex-col items-center gap-2 text-sm font-semibold" data-testid="badge-share"><Share className="h-6 w-6" />Delen / Foto's</button>
      </div>

      {!standalone && (
        <div className="w-full max-w-sm mt-6 rounded-2xl bg-slate-900 border border-slate-800 p-5 text-sm" data-testid="badge-a2hs">
          <div className="flex items-center gap-2 font-semibold mb-2"><PlusSquare className="h-5 w-5 text-orange-400" />Zet je badge op je beginscherm</div>
          {isIOS
            ? <ol className="list-decimal ml-5 space-y-1 text-slate-300"><li>Tik onderaan op <b>Delen</b> (vierkant met pijl).</li><li>Kies <b>Zet op beginscherm</b>.</li><li>Tik op <b>Voeg toe</b> — je badge staat nu als app-icoon klaar.</li></ol>
            : <ol className="list-decimal ml-5 space-y-1 text-slate-300"><li>Tik rechtsboven op <b>⋮</b>.</li><li>Kies <b>Toevoegen aan startscherm</b> of <b>App installeren</b>.</li><li>Bevestig — je badge staat nu als app-icoon klaar.</li></ol>}
          <p className="text-slate-500 text-xs mt-3">Tip: sla de afbeelding ook op als vergrendelscherm, dan hoef je je telefoon niet te ontgrendelen aan de kiosk.</p>
        </div>
      )}
      <LeaveForm token={token} />
      <p className="text-slate-600 text-[11px] mt-6 text-center max-w-sm">Deze badge is persoonlijk. Deel de link niet. Bij verlies van je telefoon vraagt de receptie een nieuwe badge aan; de oude werkt dan niet meer.</p>
    </div>
  );
}

const KIND_NL = { leave: "Verlof", sick: "Ziek" };
const ST_NL = { pending: ["In behandeling", "text-amber-400"], approved: ["Goedgekeurd", "text-emerald-400"], rejected: ["Afgewezen", "text-rose-400"] };

/* Verlof aanvragen of ziek melden vanaf de telefoon — receptie/eigenaar keurt goed in Personeel → Uren. */
function LeaveForm({ token }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ kind: "leave", start: today, end: today, note: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => axios.get(`${API}/api/badge/${token}/leave`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line
  const submit = async () => {
    setBusy(true); setMsg(null);
    try { await axios.post(`${API}/api/badge/${token}/leave`, f); setMsg({ ok: true, text: f.kind === "sick" ? "Ziekmelding doorgegeven. Beterschap!" : "Verlofaanvraag verstuurd — je krijgt de beslissing hier te zien." }); setF({ ...f, note: "" }); load(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Versturen mislukt" }); }
    finally { setBusy(false); }
  };
  const inp = "w-full rounded-xl bg-slate-800 border border-slate-700 px-3 py-2.5 text-sm text-white";
  return (
    <div className="w-full max-w-sm mt-6 rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid="badge-leave">
      <div className="flex items-center gap-2 font-semibold mb-3"><CalendarOff className="h-5 w-5 text-orange-400" />Verlof aanvragen / ziek melden</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {["leave", "sick"].map((k) => <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} className={`rounded-xl py-2.5 text-sm font-semibold transition ${f.kind === k ? (k === "sick" ? "bg-rose-600" : "bg-orange-500") : "bg-slate-800 text-slate-300"}`} data-testid={`leave-kind-${k}`}>{KIND_NL[k]}</button>)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-400">Van<input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value, end: e.target.value > f.end ? e.target.value : f.end })} className={inp} data-testid="leave-start" /></label>
        <label className="text-xs text-slate-400">Tot en met<input type="date" value={f.end} min={f.start} onChange={(e) => setF({ ...f, end: e.target.value })} className={inp} data-testid="leave-end" /></label>
      </div>
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Reden (optioneel)" maxLength={300} className={`${inp} mt-2`} data-testid="leave-note" />
      <button onClick={submit} disabled={busy} className="mt-3 w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-[.98] transition py-3 font-semibold disabled:opacity-50" data-testid="leave-submit">{busy ? "…" : "Versturen"}</button>
      {msg && <div className={`mt-3 rounded-xl p-3 text-sm ${msg.ok ? "bg-emerald-600/20 text-emerald-300" : "bg-rose-600/20 text-rose-300"}`} data-testid="leave-msg">{msg.text}</div>}
      {rows.length > 0 && <div className="mt-4 space-y-1.5" data-testid="leave-history">{rows.slice(0, 6).map((r) => <div key={r.id} className="flex justify-between text-xs text-slate-300 border-t border-slate-800 pt-1.5"><span>{KIND_NL[r.kind]} · {r.start}{r.end !== r.start ? ` → ${r.end}` : ""}</span><span className={ST_NL[r.status]?.[1]}>{ST_NL[r.status]?.[0]}</span></div>)}</div>}
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
